import type { FC, HTMLAttributes, ReactElement } from 'react';
import { useEffect } from 'react';

import { useAudioTrigger } from '../../../controllers/audioController/hooks';
import { openUrl } from '../../../hooks/helpers/openUrl';
import { useIsLobbyButtonHiddenConnected } from '../../../hooks/useIsLobbyButtonHiddenConnected';
import { useLobbyURLConnected } from '../../../hooks/useLobbyURLConnected';
import { useShouldStayConnected } from '../../../hooks/useShouldStayConnected';
import { L10nText } from '../../../services/uiTranslations/reactBindings/L10nText';
import { useCoreDispatch } from '../../../store/CoreStore';
import { useConfigSlice } from '../../../store/slices/config/selectors';
import { useGameStateSlice } from '../../../store/slices/game/selectors';
import { useHeaderSlice } from '../../../store/slices/header/selectors';
import { headerActions } from '../../../store/slices/header/slice';
import { popupActions } from '../../../store/slices/popup/slice';
import { useModifiers } from '../../../styles/hooks/useModifiers';
import { SessionTimer } from '../../compliance/SessionTimer';
import { IconButtonWithTooltip } from '../../controls/iconButton/IconButtonWithTooltip';
import { CurrencyConnected } from '../../currency/CurrencyConnected';
import { Icon } from '../../icons/icon';
import { Icons } from '../../icons/iconNames';
import { PopupType } from '../../popup/types';
import { Time } from '../../time/index';
import { Tooltip, TooltipPositions } from '../../tooltip/Tooltip';
import { OverlayLabel } from '../label/OverlayLabel';

import styles from './gameHeader.module.css';

export interface GameHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {}

export const GameHeader: FC<GameHeaderProps> = (rest): ReactElement | null => {
  const audioTrigger = useAudioTrigger();
  const compliance = useConfigSlice.compliance();
  const dispatch = useCoreDispatch();
  const gameId = useGameStateSlice.gameId();
  const isLobbyButtonHidden = useIsLobbyButtonHiddenConnected();
  const shouldStayAtTable = useShouldStayConnected();
  const tableBetLimits = useConfigSlice.tableBetLimits();
  const tableConfig = useConfigSlice.tableConfig();
  const tableName = useConfigSlice.tableName();
  const open = useHeaderSlice.getCoreBetLimitsOpen();

  // LOBBY-265: Because of 'gameLobbySymbol' key never being explicitlly cleared
  // it's preserved after redirection from a slot game to slots lobby.
  // Thus invalid 'gameLobbySymbol' can make an LC game appear in slots b2g component, but the game is not actually active.
  const lobbyUrl = useLobbyURLConnected(undefined, { gameLobbySymbol: '' });
  const showSessionTimer = useConfigSlice.isSessionTimerVisisble();

  const mods = useModifiers(styles, 'gameHeader', { open }, { withBaseClass: true });
  const showTableLimitsPopup = !!useConfigSlice.jurisdiction()?.showTableLimitsPopup;
  useEffect(() => {
    if (showTableLimitsPopup) {
      dispatch(headerActions.setBetLimitsOpen(true));
    }
  }, [showTableLimitsPopup, dispatch]);

  if (!tableName || !tableBetLimits) {
    return null;
  }

  return (
    <div data-testid="game-header" className={mods} {...rest}>
      <div className={styles.gameHeader__row}>
        {isLobbyButtonHidden ? null : (
          <IconButtonWithTooltip
            data-testid="exit-game-button"
            icon={Icons.Close}
            onClick={(): void => {
              if (shouldStayAtTable) {
                dispatch(popupActions.pushPopup(PopupType.DontLeaveRoundInProgress));
                return;
              }

              openUrl(lobbyUrl);
            }}
            tooltipOptions={{
              position: TooltipPositions.Bottom,
              text: 'EXITGAME',
            }}
          />
        )}
        <div data-testid="game-header-content" className={styles.gameHeader__content}>
          {compliance?.showSessionId ? (
            <div data-testid="game-header-compliance" className={styles.gameHeader__info_compliance}>
              <OverlayLabel small>
                ID-S: {tableConfig?.aamsSessionId} ID-P: {tableConfig?.aamsTicket}
              </OverlayLabel>
            </div>
          ) : null}
          <button
            data-testid="game-header-title-button"
            className={styles.gameHeader__titleButton}
            onClick={() => {
              dispatch(headerActions.setBetLimitsOpen(!open));
              audioTrigger.play({ name: 'menuClick' });
            }}
            type="button"
          >
            <L10nText tk={tableName} />
            <CurrencyConnected value={tableBetLimits.min} avoidTrailingZero />
            {'-'}
            <CurrencyConnected value={tableBetLimits.max} avoidTrailingZero />
            <Tooltip
              position={TooltipPositions.Bottom}
              text={open ? 'CLOSEBETLIMITGSANDPAYOUTS' : 'OPENBETLIMITGSANDPAYOUTS'}
            >
              <Icon icon={Icons.ChevronDown} className={styles.gameHeader__icon} />
            </Tooltip>
          </button>
          <div data-testid="game-header-info" className={styles.gameHeader__info}>
            {tableConfig?.showPPBranding ? <OverlayLabel>PRAGMATIC PLAY &bull;</OverlayLabel> : null}
            <OverlayLabel>
              <Time />
            </OverlayLabel>
            <OverlayLabel>
              <L10nText tk="GAME_NUMBER" />: {gameId}
            </OverlayLabel>
            <OverlayLabel>{showSessionTimer ? <SessionTimer /> : null}</OverlayLabel>
          </div>
        </div>
      </div>
    </div>
  );
};
